import { QueryClient } from "@tanstack/react-query";

import { documentsAndMessagesKeys } from "./documentsAndMessagesKeys";
import type { DocumentsAndMessagesResponse, WorkbenchDocument } from "./types";

const POLL_INTERVAL = 5000; // 5 seconds
const POLL_TIMEOUT = 60_000; // 60 seconds

/**
 * Create optimistic document for "FAX" upload.
 * Shows "Processing" in datetime column, not clickable.
 * @param proofFileId
 */
function createOptimisticDocument(proofFileId: string): WorkbenchDocument {
  return {
    docId: proofFileId,
    source: "optimistic",
    docType: "proof",
    fileName: "FAX",
    uploadedAt: "Processing",
    status: "processing",
    sizeBytes: undefined,
    uploadedByOraid: undefined,
  };
}

/**
 * Add optimistic document to cache, then poll until real document appears or timeout.
 * Removes optimistic doc after timeout if not found.
 * @param queryClient - TanStack Query client
 * @param approvalId - Deal approval ID
 * @param proofFileId - ID of uploaded proof
 */
export async function pollForOptimisticDocument(
  queryClient: QueryClient,
  approvalId: string,
  proofFileId: string
): Promise<void> {
  const queryKey = documentsAndMessagesKeys.detail({ approvalId });
  const optimisticDocument = createOptimisticDocument(proofFileId);

  // Add optimistic document to cache immediately
  queryClient.setQueryData<DocumentsAndMessagesResponse>(queryKey, (old) => {
    if (!old) {
      // Create cache entry if it doesn't exist
      return {
        data: {
          appId: Number.parseInt(approvalId, 10),
          documentCount: 1,
          unreadMessageCount: 0,
          documents: [optimisticDocument],
          messages: [],
        },
        degradedLegs: [],
        meta: {
          fetchedAt: new Date().toISOString(),
          cacheTtlSeconds: 30,
          legs: {},
          defaultFilter: "All",
          supportedFilters: ["All"],
        },
      };
    }
    return {
      ...old,
      data: {
        ...old.data,
        documents: [optimisticDocument, ...old.data.documents],
        documentCount: old.data.documentCount + 1,
      },
    };
  });

  // Poll for real document
  const startTime = Date.now();

  const pollPromise = new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;

      // Fetch fresh data from cache
      const cached = queryClient.getQueryData<DocumentsAndMessagesResponse>(queryKey);
      if (!cached) {
        clearInterval(interval);
        resolve();
        return;
      }

      // Check if real document appeared in the API response
      const realDocumentFound = cached.data.documents.some(
        (document_) => document_.docId === proofFileId && document_.status !== "processing"
      );

      if (realDocumentFound) {
        clearInterval(interval);
        resolve();
        return;
      }

      // Timeout reached — remove optimistic doc
      if (elapsed >= POLL_TIMEOUT) {
        queryClient.setQueryData<DocumentsAndMessagesResponse>(queryKey, (old) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            data: {
              ...old.data,
              documents: old.data.documents.filter((document_) => document_.docId !== proofFileId),
              documentCount: Math.max(0, old.data.documentCount - 1),
            },
          };
        });
        clearInterval(interval);
        resolve();
      }
    }, POLL_INTERVAL);
  });

  return pollPromise;
}
